import React from 'react';
import { useDrag } from 'react-dnd';
import { useQuery } from '@tanstack/react-query';
import { Loader2, AlertCircle } from 'lucide-react';
import emailTemplateService from '@/services/email-template.service';
import { TemplateVariable } from '@/types/email-template.types';
import { editorVariableName, variableLabel } from '@/utils/react-email-converter';

// Fallback common variables when no template type is selected or API fails
const FALLBACK_VARIABLES: TemplateVariable[] = [
  { name: 'user_name', type: 'string', description: 'User full name', required: false },
  { name: 'first_name', type: 'string', description: 'User first name', required: false },
  { name: 'last_name', type: 'string', description: 'User last name', required: false },
  { name: 'email', type: 'string', description: 'User email address', required: false },
  { name: 'company_name', type: 'string', description: 'Company/Organization name', required: false },
  { name: 'date', type: 'date', description: 'Current date', required: false },
];

interface VariablesPanelProps {
  templateType?: string;
}

interface DraggableVariableProps {
  name: string;
  label: string;
  description?: string;
  required?: boolean;
}

const DraggableVariable: React.FC<DraggableVariableProps> = ({ name, label, description, required }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'VARIABLE',
    item: { name, label },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  return (
    <div
      ref={drag}
      className={`px-3 py-2 bg-blue-50 border border-blue-200 rounded cursor-move hover:bg-blue-100 hover:border-blue-400 transition-all ${
        isDragging ? 'opacity-50' : 'opacity-100'
      }`}
      title={description || name}
    >
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700">
        <span className="px-1.5 py-0.5 bg-blue-200 rounded text-[10px] font-bold">VAR</span>
        {label}
        {required && <span className="text-red-500">*</span>}
      </span>
    </div>
  );
};

export default function VariablesPanel({ templateType }: VariablesPanelProps) {
  // Fetch templates by type to get allowed_variables
  const { data: templates, isLoading, error } = useQuery({
    queryKey: ['emailTemplateVariables', templateType],
    queryFn: () => emailTemplateService.getTemplatesByType(templateType!),
    enabled: !!templateType,
    staleTime: 5 * 60 * 1000,
  });

  // Extract unique variables from all templates of this type
  // API returns allowed_variables as string[] (e.g., ["user_name", "company_name"])
  const variables = React.useMemo(() => {
    if (!templates || templates.length === 0) {
      return FALLBACK_VARIABLES.map(v => v.name);
    }

    const variableSet = new Set<string>();

    templates.forEach(template => {
      if (template.allowed_variables) {
        const vars = template.allowed_variables as Array<string | TemplateVariable>;
        vars.forEach(variable => {
          const name = typeof variable === 'string' ? variable : variable.name;
          if (name) variableSet.add(editorVariableName(name));
        });
      }
    });

    const result = Array.from(variableSet);
    return result.length > 0 ? result : FALLBACK_VARIABLES.map(v => v.name);
  }, [templates]);

  return (
    <div className="bg-white border-r flex flex-col max-h-[calc(100vh-57px)] overflow-y-auto">
      <div className="p-4 border-b">
        <h3 className="text-xs font-semibold text-gray-500 uppercase">
          {templateType ? 'Template Variables' : 'Common Variables'}
        </h3>
      </div>

      <div className="p-4">
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-500">Loading variables...</span>
          </div>
        )}

        {error && !isLoading && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-3">
            <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <span className="text-xs text-amber-700">Using default variables</span>
          </div>
        )}

        {!isLoading && (
          <div className="space-y-2">
            {variables.length > 0 ? (
              variables.map((name, index) => (
                <DraggableVariable
                  key={`${name}-${index}`}
                  name={name}
                  label={variableLabel(name, name)}
                />
              ))
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No variables found</p>
            )}
          </div>
        )}

        {!templateType && !isLoading && (
          <p className="text-xs text-gray-400 mt-4 text-center">
            Select a template type to see specific variables
          </p>
        )}
      </div>
    </div>
  );
}
